import { getDownloadURL, ref, uploadBytes, deleteObject } from 'firebase/storage';
import { storage } from 'src/config/firebase';

export interface UploadedChatMediaResult {
  url: string;
  filePath: string;
  fileName: string;
  sizeBytes: number;
}

/**
 * Delete a chat media item from Firebase Storage (by storage path or download URL)
 */
export const deleteChatMedia = async (urlOrPath: string): Promise<void> => {
  try {
    if (!urlOrPath) return;
    const storageRef = ref(storage, urlOrPath);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting chat media from storage:', error);
  }
};

export const uploadTrainerFile = async (
  file: File,
  folder: 'avatars' | 'certificates' | 'videos',
  userId: string,
): Promise<string> => {
  const fileExtension = file.name.split('.').pop();
  const fileName = `${crypto.randomUUID()}.${fileExtension}`;
  // The user requested to use the "trainer_profile_images" bucket or folder.
  // We'll store it under trainer_profile_images/{folder}/{userId}/{fileName}
  // For certificates, store under trainer_profile_certificate/{userId}/{fileName}
  // For videos, store under trainer_profile_video/{userId}/{fileName}
  let filePath = '';
  if (folder === 'certificates') {
    filePath = `trainer_profile_certificate/${userId}/${fileName}`;
  } else if (folder === 'videos') {
    filePath = `trainer_profile_video/${userId}/${fileName}`;
  } else {
    filePath = `trainer_profile_images/${folder}/${userId}/${fileName}`;
  }
  const storageRef = ref(storage, filePath);

  await uploadBytes(storageRef, file);

  return await getDownloadURL(storageRef);
};

export const uploadSupportScreenshot = async (file: File, userId: string): Promise<string> => {
  const fileExtension = file.name.split('.').pop();
  const timestamp = new Date().getTime();
  const fileName = `${timestamp}_${crypto.randomUUID()}.${fileExtension}`;

  const filePath = `trainer_support_reports/${userId}/${fileName}`;
  const storageRef = ref(storage, filePath);

  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

export const deleteSupportScreenshot = async (urlOrPath: string): Promise<void> => {
  try {
    if (!urlOrPath) return;
    const storageRef = ref(storage, urlOrPath);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting support screenshot from storage:', error);
  }
};

/**
 * Upload chat audio voice notes under `chat_media/audio/{chatId}/{fileName}`
 */
export const uploadChatAudio = async (
  blob: Blob,
  chatId: string,
  _userId: string,
): Promise<string> => {
  const isMp3 = blob.type.includes('mp3') || blob.type.includes('mpeg');
  const isM4a = blob.type.includes('mp4') || blob.type.includes('m4a');
  const fileExtension = isMp3 ? 'mp3' : isM4a ? 'm4a' : 'webm';
  const timestamp = new Date().getTime();
  const fileName = `${timestamp}_${crypto.randomUUID()}.${fileExtension}`;

  const filePath = `chat_media/audio/${chatId}/${fileName}`;
  const storageRef = ref(storage, filePath);

  await uploadBytes(storageRef, blob, {
    contentType:
      blob.type || (isMp3 ? 'audio/mp3' : isM4a ? 'audio/mp4' : 'audio/webm;codecs=opus'),
  });

  return await getDownloadURL(storageRef);
};

/**
 * Upload chat images under `chat_media/images/{chatId}/{fileName}`
 */
export const uploadChatImage = async (
  file: File,
  chatId: string,
  _userId?: string,
): Promise<UploadedChatMediaResult> => {
  const fileExtension = file.name.split('.').pop() || 'jpg';
  const timestamp = new Date().getTime();
  const fileName = `${timestamp}_${crypto.randomUUID()}.${fileExtension}`;

  const filePath = `chat_media/images/${chatId}/${fileName}`;
  const storageRef = ref(storage, filePath);

  await uploadBytes(storageRef, file, {
    contentType: file.type || 'image/jpeg',
  });

  const url = await getDownloadURL(storageRef);
  return {
    url,
    filePath,
    fileName: file.name,
    sizeBytes: file.size,
  };
};

/**
 * Upload chat videos under `chat_media/videos/{chatId}/{fileName}`
 */
export const uploadChatVideo = async (
  file: File,
  chatId: string,
  _userId?: string,
): Promise<UploadedChatMediaResult> => {
  const fileExtension = file.name.split('.').pop() || 'mp4';
  const timestamp = new Date().getTime();
  const fileName = `${timestamp}_${crypto.randomUUID()}.${fileExtension}`;

  const filePath = `chat_media/videos/${chatId}/${fileName}`;
  const storageRef = ref(storage, filePath);

  await uploadBytes(storageRef, file, {
    contentType: file.type || 'video/mp4',
  });

  const url = await getDownloadURL(storageRef);
  return {
    url,
    filePath,
    fileName: file.name,
    sizeBytes: file.size,
  };
};

export interface UploadedChatFileResult {
  url: string;
  filePath: string;
  fileName: string;
  sizeBytes: number;
  mimeType: string;
}

/**
 * Upload chat files/documents under `chat_media/files/{chatId}/{fileName}`
 */
export const uploadChatFile = async (
  file: File,
  chatId: string,
  _userId?: string,
): Promise<UploadedChatFileResult> => {
  const timestamp = new Date().getTime();
  const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const storageFileName = `${timestamp}_${crypto.randomUUID()}_${sanitizedName}`;

  const filePath = `chat_media/files/${chatId}/${storageFileName}`;
  const storageRef = ref(storage, filePath);

  await uploadBytes(storageRef, file, {
    contentType: file.type || 'application/octet-stream',
  });

  const downloadUrl = await getDownloadURL(storageRef);
  return {
    url: downloadUrl,
    filePath,
    fileName: file.name,
    sizeBytes: file.size,
    mimeType: file.type,
  };
};
